import React, { useEffect, useRef, useState } from 'react';
import { Dialog, DialogContent, DialogTitle, DialogDescription } from './ui/dialog';
import { Button } from './ui/button';
import { Lock, Shield, CheckCircle2, X, ArrowRight, Sparkles, CalendarClock, RefreshCw, AlertTriangle, RotateCw } from 'lucide-react';
import { api } from '../api';
import { contactInfo } from '../mock';
import { useAuth } from '../context/AuthContext';
import { PLANS } from './PlanComparison';

const WHATSAPP_SUPPORT = contactInfo.whatsappUrl;
const fmtDate = (d) => new Date(d).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });

// Recurring Razorpay subscription: card/UPI mandate now, first charge only after the 7-day free period.
const Checkout = ({ open, onClose, plan, onSuccess }) => {
  const { user, refresh } = useAuth();
  const [saving, setSaving] = useState(false);
  const [agreed, setAgreed] = useState(false);
  const [done, setDone] = useState(null);

  const [trial, setTrial] = useState(null);
  const [trialLoaded, setTrialLoaded] = useState(false);
  useEffect(() => {
    if (!open) return;
    setTrialLoaded(false);
    setAgreed(false);
    api.trialStatus().then(setTrial).catch(() => setTrial(null)).finally(() => setTrialLoaded(true));
  }, [open, plan]);

  const p = PLANS.find((x) => x.key === plan) || { name: 'Plan', price: 0 };
  // First charge = end of the free period (server is authoritative; this mirrors it for display)
  // No trial yet (or status unavailable) → server defers the first charge by 7 days
  const trialEnd = trial?.trialEnd ? new Date(trial.trialEnd) : (!trial || !trial.trialStart ? new Date(Date.now() + 7 * 864e5) : null);
  const firstCharge = trialEnd && trialEnd.getTime() - Date.now() > 30 * 60 * 1000 ? trialEnd : null;

  const [issue, setIssue] = useState(null); // { kind, message }
  const failed = useRef(false);
  const issueRef = useRef(null);
  useEffect(() => { if (issue) setTimeout(() => issueRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' }), 50); }, [issue]);

  const explain = (err) => {
    const txt = `${err?.description || ''} ${err?.reason || ''} ${err?.code || ''}`.toLowerCase();
    if (txt.includes('website') || txt.includes('unregistered')) {
      return 'Payments are briefly unavailable while our payment partner verifies this website. Please try again shortly, or message us on WhatsApp and we\'ll help you finish.';
    }
    return err?.description || 'The payment could not be completed. No money was deducted.';
  };

  const checkStatus = async () => {
    setConfirming(true);
    const ok = await checkOnce();
    setConfirming(false);
    if (!ok) setIssue({ kind: 'pending', message: "We haven't received confirmation yet. If you approved the mandate, it will confirm automatically in a few seconds — or try again." });
  };

  const poll = useRef(null);
  const rzpRef = useRef(null);
  const confirmedRef = useRef(false);
  const [confirming, setConfirming] = useState(false);
  const stopPolling = () => { if (poll.current) { clearInterval(poll.current); poll.current = null; } };
  useEffect(() => () => stopPolling(), []);

  // Single success path used by the checkout callback, live polling and status checks
  const confirmed = async (subscription) => {
    if (confirmedRef.current) return;
    confirmedRef.current = true;
    stopPolling();
    try { rzpRef.current && rzpRef.current.close(); } catch { /* already closed */ }
    setIssue(null); setConfirming(false); setSaving(false);
    setDone(subscription || { firstChargeAt: null });
    if (refresh) refresh();
    setTimeout(() => onSuccess && onSuccess(subscription), 1200);
  };

  const createdId = useRef(null);
  // Prepare the Razorpay subscription while the user reads the summary → the button opens checkout instantly
  const preSub = useRef(null);
  useEffect(() => {
    preSub.current = null;
    if (!open || !plan) return;
    const pr = api.createSubscription({ plan }).then((r) => ({ plan, ...r }));
    pr.catch(() => {});
    preSub.current = { plan, promise: pr };
  }, [open, plan]);
  const checkOnce = async () => {
    try {
      const st = await api.subscriptionStatus(createdId.current);
      const matches = !createdId.current || st.subscription?.razorpaySubscriptionId === createdId.current;
      if (st.state === 'active' && matches) { await confirmed(st.subscription); return true; }
    } catch { /* network blip — keep polling */ }
    return false;
  };

  const startPolling = () => {
    stopPolling();
    const started = Date.now();
    poll.current = setInterval(async () => {
      if (Date.now() - started > 15 * 60 * 1000) return stopPolling();
      await checkOnce();
    }, 2000);
  };

  const startPayment = async () => {
    if (typeof window.Razorpay === 'undefined') {
      setIssue({ kind: 'error', message: 'The secure payment window could not load. Check your connection and tap Try again.' });
      return;
    }
    setIssue(null);
    failed.current = false;
    confirmedRef.current = false;
    setSaving(true);
    try {
      let sub = null;
      if (preSub.current && preSub.current.plan === plan) {
        try { sub = await preSub.current.promise; } catch { sub = null; }
      }
      if (!sub) sub = await api.createSubscription({ plan });
      createdId.current = sub.subscription_id;
      // Keep the payment window light: pause session recording of the payment iframe
      try { window.posthog && window.posthog.stopSessionRecording && window.posthog.stopSessionRecording(); } catch { /* noop */ }
      const rzp = new window.Razorpay({
        key: sub.key_id,
        subscription_id: sub.subscription_id,
        name: 'The Brain Theory',
        description: sub.firstChargeAt
          ? `${p.name} · 7 days free, then ₹${p.price}/month`
          : `${p.name} · ₹${p.price}/month`,
        prefill: { name: user?.name || '', email: user?.email || '', contact: user?.mobile || '' },
        theme: { color: '#F4C430' },
        modal: {
          ondismiss: async () => {
            if (confirmedRef.current) return;
            // The mandate may already be approved in the UPI app — confirm before saying it isn't
            setConfirming(true);
            let ok = await checkOnce();
            for (let i = 0; !ok && i < 2; i += 1) {  // status checks are throttled server-side; retry briefly
              await new Promise((r) => setTimeout(r, 3000));
              ok = confirmedRef.current || await checkOnce();
            }
            setConfirming(false);
            if (ok || confirmedRef.current) return;
            setSaving(false);
            if (!failed.current) setIssue({ kind: 'dismissed', message: 'Autopay is not set up yet. No money was deducted — pick up where you left off.' });
          },
          confirm_close: false,
        },
        retry: { enabled: true, max_count: 3 },
        handler: async (resp) => {
          try {
            const r = await api.verifySubscription({
              razorpay_payment_id: resp.razorpay_payment_id,
              razorpay_subscription_id: resp.razorpay_subscription_id,
              razorpay_signature: resp.razorpay_signature,
              plan,
            });
            await confirmed(r.subscription);
          } catch (err) {
            if (await checkOnce()) return;
            setSaving(false);
            setIssue({ kind: 'verify', message: `We're confirming your autopay with Razorpay (ref ${resp.razorpay_payment_id}). This usually takes a few seconds.` });
            startPolling();
          }
        },
      });
      rzpRef.current = rzp;
      rzp.on('payment.failed', (resp) => {
        failed.current = true;
        setSaving(false);
        setIssue({ kind: 'failed', message: explain(resp?.error) });
      });
      rzp.open();
      startPolling();
    } catch (err) {
      setSaving(false);
      setIssue({ kind: 'error', message: err.message || 'Could not start the secure checkout. Please try again.' });
    }
  };

  const close = () => { stopPolling(); setDone(null); setSaving(false); setIssue(null); onClose && onClose(); };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && close()}>
      <DialogContent className="w-[calc(100vw-24px)] max-w-lg bg-[#2A1810] border border-[#F4C430]/30 text-[#F5EDE0] p-0 overflow-hidden rounded-3xl [&>button:not([data-testid])]:hidden max-h-[90vh] overflow-y-auto" data-testid="checkout-dialog">
        <DialogTitle className="sr-only">Set up your {p.name} plan</DialogTitle>
        <DialogDescription className="sr-only">7 days free, then ₹{p.price} per month, renews automatically. Cancel anytime.</DialogDescription>
        <button onClick={close} aria-label="Close" className="absolute top-4 right-4 h-9 w-9 rounded-full grid place-items-center border border-[#5a3d24] text-[#D9C9B3] hover:text-[#F4C430] hover:border-[#F4C430] z-10" data-testid="checkout-close-btn">
          <X size={16} />
        </button>

        {done ? (
          <div className="p-8 text-center" data-testid="checkout-success">
            <div className="mx-auto h-14 w-14 rounded-full grid place-items-center bg-[#F4C430]/20 border border-[#F4C430]/50">
              <CheckCircle2 className="text-[#F4C430]" size={28} />
            </div>
            <h3 className="mt-4 font-serif text-2xl">Autopay is set up!</h3>
            <p className="mt-2 text-[#B8A990] text-sm">
              {done.firstChargeAt
                ? <>Enjoy full access free until <span className="text-[#F4C430]">{fmtDate(done.firstChargeAt)}</span>. Your {p.name} plan (₹{p.price}/month) starts after that.</>
                : <>Your <span className="text-[#F4C430]">{p.name}</span> plan is active.</>}
            </p>
          </div>
        ) : (
          <div className="p-6 sm:p-8">
            <div className="inline-flex items-center gap-2 rounded-full bg-[#3D2817] border border-[#5a3d24] px-3 py-1 text-xs text-[#F4C430]">
              <Sparkles size={13} /> Secure Razorpay autopay
            </div>
            <h3 className="mt-4 font-serif text-2xl sm:text-3xl leading-tight">
              Start your <span className="italic text-[#F4C430]">{p.name}</span> plan
            </h3>

            {!trialLoaded ? (
              <div className="mt-5 space-y-2" data-testid="checkout-loading">
                {[0, 1, 2].map((i) => <div key={i} className="h-16 rounded-2xl bg-[#3D2817] border border-[#4a3221] animate-pulse" />)}
              </div>
            ) : (
            <ol className="mt-5 rounded-2xl bg-[#3D2817] border border-[#4a3221] divide-y divide-[#4a3221]" data-testid="checkout-timeline">
              <li className="p-4 flex items-start gap-3">
                <span className="h-8 w-8 rounded-full grid place-items-center bg-[#F4C430] text-[#2A1810] text-xs font-bold shrink-0">1</span>
                <div>
                  <div className="text-sm font-medium">Today: {firstCharge ? 'No plan charge — 7 days of full free access' : `₹${p.price} charged`}</div>
                  <div className="text-xs text-[#B8A990]">Set up autopay with UPI, card or netbanking. Razorpay may place a small refundable verification charge.</div>
                </div>
              </li>
              {firstCharge && (
                <li className="p-4 flex items-start gap-3">
                  <span className="h-8 w-8 rounded-full grid place-items-center border border-[#F4C430] text-[#F4C430] shrink-0"><CalendarClock size={14} /></span>
                  <div>
                    <div className="text-sm font-medium" data-testid="checkout-first-charge">{fmtDate(firstCharge)}: ₹{p.price} — first monthly charge</div>
                    <div className="text-xs text-[#B8A990]">Billing starts only after your 7 free days end.</div>
                  </div>
                </li>
              )}
              <li className="p-4 flex items-start gap-3">
                <span className="h-8 w-8 rounded-full grid place-items-center border border-[#5a3d24] text-[#D9C9B3] shrink-0"><RefreshCw size={14} /></span>
                <div>
                  <div className="text-sm font-medium">Then ₹{p.price} every month, automatically</div>
                  <div className="text-xs text-[#B8A990]">Cancel anytime from Profile → Subscription. No charges after you cancel.</div>
                </div>
              </li>
            </ol>
            )}

            {trialLoaded && (
              <div className="mt-3 rounded-xl bg-[#3D2817] border border-[#4a3221] p-3 sm:p-4" data-testid="checkout-autopay-rules">
                <div className="text-xs uppercase tracking-widest text-[#F4C430]">How autopay works</div>
                <ul className="mt-2 space-y-1.5 text-xs text-[#D9C9B3] list-disc pl-4">
                  <li>No money is deducted today{firstCharge ? <> — deductions start only after your 7 free days, on <span className="text-[#F5EDE0]">{fmtDate(firstCharge)}</span></> : ''}.</li>
                  <li>₹{p.price} is then charged automatically every month on the same date until you cancel.</li>
                  <li>Your bank / UPI app sends a pre-debit notification before every charge, as required by RBI e-mandate rules.</li>
                  <li>Cancel anytime from Profile → Subscription (or your UPI app's mandates). Cancel before {firstCharge ? fmtDate(firstCharge) : 'your next billing date'} and you won't be charged at all.</li>
                  <li>Razorpay places a small verification amount (usually ₹5) to confirm your mandate — it is refunded automatically.</li>
                </ul>
              </div>
            )}

            <div className="mt-3 rounded-xl bg-[#3D2817] border border-[#4a3221] p-3 flex items-start gap-2.5">
              <Shield size={14} className="text-[#F4C430] mt-0.5 shrink-0" />
              <div className="text-xs text-[#B8A990]">
                Your payment details are entered on Razorpay's secure checkout and never stored by The Brain Theory.
              </div>
            </div>

            {confirming && (
              <div className="mt-4 rounded-2xl border border-[#F4C430]/40 bg-[#3D2817] p-4 flex items-center gap-3" role="status" data-testid="checkout-confirming">
                <RefreshCw size={16} className="text-[#F4C430] animate-spin" />
                <span className="text-sm text-[#F5EDE0]">Confirming your autopay with Razorpay…</span>
              </div>
            )}
            {issue && !confirming && (
              <div ref={issueRef} role="alert" className={`mt-4 rounded-2xl border p-4 ${['verify', 'pending'].includes(issue.kind) ? 'border-[#F4C430]/40 bg-[#3D2817]' : 'border-orange-400/50 bg-orange-500/10'}`} data-testid="checkout-issue">
                <div className="flex items-start gap-2.5">
                  {['verify', 'pending'].includes(issue.kind)
                    ? <RefreshCw size={17} className="text-[#F4C430] shrink-0 mt-0.5 animate-spin" />
                    : <AlertTriangle size={18} className="text-orange-300 shrink-0 mt-0.5" />}
                  <div className="text-sm text-[#F5EDE0]" data-testid="checkout-issue-message">{issue.message}</div>
                </div>
                <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-2">
                  {issue.kind === 'verify' || issue.kind === 'pending' ? (
                    <Button onClick={checkStatus} data-testid="checkout-check-status" className="rounded-full bg-[#F4C430] hover:bg-[#FFD34E] text-[#2A1810] font-semibold h-11">Check status</Button>
                  ) : (
                    <Button onClick={startPayment} disabled={saving} data-testid="checkout-retry" className="rounded-full bg-[#F4C430] hover:bg-[#FFD34E] text-[#2A1810] font-semibold h-11">
                      <RotateCw size={15} className="mr-1.5" /> Try again
                    </Button>
                  )}
                  <Button onClick={startPayment} disabled={saving} variant="outline" data-testid="checkout-other-method" className="rounded-full border-[#5a3d24] bg-transparent text-[#F5EDE0] hover:bg-[#4a3221] hover:text-[#F4C430] h-11">
                    Try a different method
                  </Button>
                </div>
                <div className="mt-2 text-xs text-[#B8A990]">UPI, QR, cards and netbanking are available in the next step. Need help? <a href={WHATSAPP_SUPPORT} target="_blank" rel="noopener noreferrer" className="text-[#F4C430] hover:underline" data-testid="checkout-whatsapp-help">WhatsApp us</a></div>
              </div>
            )}

            <label className="mt-4 flex items-start gap-2.5 text-xs text-[#D9C9B3] cursor-pointer select-none">
              <input type="checkbox" checked={agreed} onChange={(e) => setAgreed(e.target.checked)}
                className="mt-0.5 h-4 w-4 accent-[#F4C430] shrink-0" data-testid="checkout-consent" />
              <span>I understand that autopay starts {firstCharge ? `on ${fmtDate(firstCharge)}` : 'today'} at ₹{p.price}/month and I can cancel anytime.</span>
            </label>
            <Button data-testid="checkout-submit-btn" onClick={startPayment} disabled={saving || !trialLoaded || !agreed}
              className="mt-5 w-full rounded-full bg-[#F4C430] hover:bg-[#FFD34E] text-[#2A1810] font-semibold h-12">
              {saving ? 'Opening secure checkout…' : <>{firstCharge ? 'Start 7 days free & set up autopay' : `Pay ₹${p.price} & activate`} <ArrowRight size={16} className="ml-1" /></>}
            </Button>
            <p className="mt-2 text-center text-[11px] text-[#8f7658]">
              By continuing you authorise a recurring ₹{p.price}/month charge{firstCharge ? ` starting ${fmtDate(firstCharge)}` : ''} until you cancel.
            </p>
            <div className="mt-1 text-center text-[11px] text-[#8f7658] inline-flex items-center gap-1 w-full justify-center">
              <Lock size={11} /> Powered by Razorpay
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default Checkout;
